import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import GoogleLoginBtn from "@/components/GoogleLoginBtn";

function validarDniNie(valor) {
  const v = (valor || "").toUpperCase().replace(/[\s-]/g, "");
  const tabla = "TRWAGMYFPDXBNJZSQVHLCKE";
  if (/^[0-9]{8}[A-Z]$/.test(v)) {
    return tabla[parseInt(v.slice(0, 8), 10) % 23] === v[8];
  }
  if (/^[XYZ][0-9]{7}[A-Z]$/.test(v)) {
    const prefijo = { X: "0", Y: "1", Z: "2" }[v[0]];
    return tabla[parseInt(prefijo + v.slice(1, 8), 10) % 23] === v[8];
  }
  return true;
}

export default function MiPerfil() {
  const { t } = useTranslation();
  const { user, login, logout } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [form, setForm] = useState({ email: "", password: "", name: "", dni: "" });
  const [dniError, setDniError] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifDni, setVerifDni] = useState("");
  const [verifDniError, setVerifDniError] = useState("");
  const [verificando, setVerificando] = useState(false);
  const [ampliarFor, setAmpliarFor] = useState(null); // reserva siendo ampliada
  const [nuevaFechaDevolucion, setNuevaFechaDevolucion] = useState("");
  const [ampliando, setAmpliando] = useState(false);

  const loadReservations = () => {
    if (!user?.email) return;
    axios.get(`/api/reservations/mine?email=${encodeURIComponent(user.email)}`)
      .then(r => setReservations(r.data || []))
      .catch(() => {});
  };
  useEffect(() => { loadReservations(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);

  // Vuelta desde Stripe tras pagar (o cancelar) la ampliación de una reserva
  useEffect(() => {
    const sessionId = params.get("ampliacion_session_id");
    const cancelada = params.get("ampliacion_cancelada");
    if (sessionId && user) {
      axios.get(`/api/reservations/ampliacion/${sessionId}/estado`)
        .then(r => {
          if (r.data.status === "paid") { toast.success(t("miPerfil.ampliacionOk")); loadReservations(); }
          else toast.error(t("miPerfil.ampliacionError"));
        })
        .catch(() => toast.error(t("miPerfil.ampliacionError")))
        .finally(() => nav("/mi-perfil", { replace: true }));
    } else if (cancelada) {
      toast(t("miPerfil.ampliacionCancelada"));
      nav("/mi-perfil", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const abrirAmpliar = (r) => {
    setAmpliarFor(r);
    setNuevaFechaDevolucion((r.fecha_devolucion || "").slice(0, 16));
  };

  const confirmarAmpliacion = async () => {
    if (!nuevaFechaDevolucion) return;
    setAmpliando(true);
    try {
      const { data } = await axios.post(`/api/reservations/${ampliarFor.id}/ampliar`, {
        nueva_fecha_devolucion: nuevaFechaDevolucion, origin_url: window.location.origin,
      });
      if (data.metodo === "tarjeta_guardada") {
        toast.success(t("miPerfil.ampliacionCobrada", { importe: data.importe_cobrado.toFixed(2) }));
        setAmpliarFor(null);
        loadReservations();
      } else if (data.url) {
        window.location.href = data.url; // pagar la diferencia como en el checkout
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || t("miPerfil.ampliacionError"));
    } finally {
      setAmpliando(false);
    }
  };

  // Vuelta desde Didit tras validar los documentos de la cuenta (alta o "Mi Perfil")
  useEffect(() => {
    const verificationId = params.get("verification_id");
    if (!verificationId || !user) return;
    axios.get(`/api/verificacion/${verificationId}/estado`)
      .then(async (r) => {
        if (r.data.status === "Approved") {
          const { data: fresh } = await axios.get("/api/auth/me");
          login({ ...user, dni: fresh.dni, documento_verificado_status: fresh.documento_verificado_status });
          toast.success(t("miPerfil.verifOk"));
        } else {
          toast.error(t("miPerfil.verifNoAprobada"));
        }
      })
      .catch(() => toast.error(t("miPerfil.verifError")))
      .finally(() => nav("/mi-perfil", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "register" && !validarDniNie(form.dni)) {
      setDniError(t("miPerfil.dniInvalido"));
      return;
    }
    setBusy(true);
    try {
      const url = mode === "login" ? "/api/auth/login" : "/api/auth/register";
      const body = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name, dni: form.dni };
      const { data } = await axios.post(url, body);
      login({
        id: data.id, email: data.email, name: data.name, token: data.token,
        dni: data.dni, documento_verificado_status: data.documento_verificado_status,
      });
      // Alta con email/contraseña: ya tenemos DNI, lanzamos Didit de inmediato.
      if (mode === "register" && data.documento_verificado_status !== "Approved") {
        await lanzarVerificacionCuenta(data.id, data.name, data.dni, data.email);
      }
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : t("miPerfil.errorAuth"));
    } finally {
      setBusy(false);
    }
  };

  const lanzarVerificacionCuenta = async (userId, nombre, dni, email) => {
    setVerificando(true);
    try {
      const { data } = await axios.post("/api/verificacion/iniciar", {
        driver: "registro", user_id: userId, nombre, dni, email, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || t("miPerfil.verifError"));
      setVerificando(false);
    }
  };

  const iniciarVerificacionCuenta = () => {
    let dniAUsar = user.dni;
    if (!dniAUsar) {
      if (!validarDniNie(verifDni)) { setVerifDniError(t("miPerfil.dniInvalido")); return; }
      dniAUsar = verifDni.toUpperCase();
    }
    lanzarVerificacionCuenta(user.id, user.name, dniAUsar, user.email);
  };

  if (!user) {
    return (
      <section className="section-light py-16" data-testid="mi-perfil-login">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <div className="divider-red mb-3"></div>
          <h1 className="font-display text-3xl mb-6">{mode === "login" ? t("miPerfil.entrar") : t("miPerfil.crearCuenta")}</h1>
          <div className="bg-black text-white p-6 rounded-lg space-y-4">
            <form onSubmit={submit} className="space-y-3" data-testid="customer-auth-form">
              {mode === "register" && (
                <>
                  <input
                    required
                    className="pc-input text-black"
                    placeholder={t("miPerfil.nombreCompleto")}
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    data-testid="auth-name"
                  />
                  <input
                    required
                    className="pc-input text-black"
                    placeholder={t("miPerfil.dniPasaporte")}
                    value={form.dni}
                    onChange={e => { setForm({ ...form, dni: e.target.value }); if (dniError) setDniError(""); }}
                    onBlur={e => setDniError(validarDniNie(e.target.value) ? "" : t("miPerfil.dniInvalido"))}
                    data-testid="auth-dni"
                  />
                  {dniError && <div className="text-red-400 text-xs" data-testid="auth-dni-error">{dniError}</div>}
                </>
              )}
              <input
                required type="email"
                className="pc-input text-black"
                placeholder={t("miPerfil.email")}
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                data-testid="auth-email"
              />
              <input
                required type="password" minLength={8}
                className="pc-input text-black"
                placeholder={t("miPerfil.contrasena")}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                data-testid="auth-password"
              />
              {err && <div className="text-sm text-[var(--pc-red)]" data-testid="auth-error">{err}</div>}
              <button disabled={busy} type="submit" className="btn-red w-full py-3" data-testid="auth-submit">
                {busy ? "..." : (mode === "login" ? t("miPerfil.entrar") : t("miPerfil.crearCuenta"))}
              </button>
            </form>
            <button
              type="button"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
              className="text-xs text-neutral-400 underline w-full"
              data-testid="auth-toggle-mode"
            >
              {mode === "login" ? t("miPerfil.noTienesCuenta") : t("miPerfil.yaTienesCuenta")}
            </button>
            <div className="border-t border-neutral-800 pt-4">
              <div className="text-xs text-neutral-500 mb-2 text-center">{t("miPerfil.continuaCon")}</div>
              <GoogleLoginBtn />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section-light py-10" data-testid="mi-perfil-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="divider-red mb-3"></div>
            <h1 className="font-display text-3xl sm:text-4xl">{t("miPerfil.miPerfilTitulo")}</h1>
            <p className="text-sm text-neutral-600 mt-1">{user.name} · {user.email}</p>
          </div>
          <button onClick={logout} className="text-xs px-3 py-2 bg-neutral-200 rounded" data-testid="logout-btn">{t("miPerfil.cerrarSesion")}</button>
        </div>

        {user.documento_verificado_status === "Approved" ? (
          <div className="mb-6 text-sm text-green-700 bg-green-50 border border-green-200 px-4 py-2 rounded inline-block" data-testid="mi-perfil-verificado-ok">
            ✓ {t("miPerfil.identidadVerificada")}
          </div>
        ) : (
          <div className="mb-6 bg-[var(--pc-grey)] p-5 rounded-lg" data-testid="mi-perfil-verificacion-pendiente">
            <h3 className="font-display text-lg mb-2">{t("miPerfil.verificaTuIdentidad")}</h3>
            <p className="text-sm text-neutral-600 mb-3">{t("miPerfil.verificaTuIdentidadDesc")}</p>
            {!user.dni && (
              <div className="mb-3 max-w-xs">
                <input
                  className="pc-input"
                  placeholder={t("miPerfil.dniPasaporte")}
                  value={verifDni}
                  onChange={e => { setVerifDni(e.target.value); if (verifDniError) setVerifDniError(""); }}
                  data-testid="verif-cuenta-dni"
                />
                {verifDniError && <div className="text-red-600 text-xs mt-1">{verifDniError}</div>}
              </div>
            )}
            <button
              type="button" onClick={iniciarVerificacionCuenta} disabled={verificando}
              className="text-xs px-3 py-2 bg-neutral-800 text-white rounded hover:bg-neutral-700 disabled:opacity-50"
              data-testid="verif-cuenta-btn"
            >
              {verificando ? t("miPerfil.verificando") : t("miPerfil.validarDocumentos")}
            </button>
          </div>
        )}

        <h2 className="font-display text-2xl mb-4">{t("miPerfil.misReservas")}</h2>
        {reservations.length === 0 && (
          <div className="bg-[var(--pc-grey)] p-6 rounded text-sm text-neutral-700">
            {t("miPerfil.sinReservas")} <Link to="/vehiculos" className="text-[var(--pc-red)] font-semibold">{t("miPerfil.pillaCoche")}</Link>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reservations.map(r => (
            <div key={r.id} className="border border-neutral-200 rounded-lg p-5 bg-white" data-testid={`my-reservation-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-display text-lg">{r.vehicle_name}</h3>
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">{t("miPerfil.pagada")}</span>
              </div>
              <div className="text-sm text-neutral-600 space-y-1 mb-3">
                <div>{t("miPerfil.recogida")}: <strong>{r.fecha_recogida?.slice(0,16).replace("T"," ")}</strong></div>
                <div>{t("miPerfil.devolucion")}: <strong>{r.fecha_devolucion?.slice(0,16).replace("T"," ")}</strong></div>
                <div>{r.dias} {t("miPerfil.diasTotal")} <strong>{r.total.toFixed(2)} €</strong></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/reservations/${r.id}/contract.pdf?token=${encodeURIComponent(user.token || "")}`}
                  target="_blank" rel="noreferrer"
                  className="btn-red inline-block px-4 py-2 text-sm"
                  data-testid={`download-contract-${r.id}`}
                >
                  {t("miPerfil.descargarContrato")}
                </a>
                <button
                  type="button" onClick={() => abrirAmpliar(r)}
                  className="px-4 py-2 text-sm bg-neutral-200 rounded hover:bg-neutral-300"
                  data-testid={`ampliar-reserva-${r.id}`}
                >
                  {t("miPerfil.ampliarAlquiler")}
                </button>
              </div>
            </div>
          ))}
        </div>

        {ampliarFor && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md" data-testid="ampliar-modal">
              <h3 className="font-display text-2xl mb-2">{t("miPerfil.ampliarAlquiler")}</h3>
              <p className="text-sm text-neutral-600 mb-4">
                {t("miPerfil.ampliarDesc", { fecha: (ampliarFor.fecha_devolucion || "").slice(0, 16).replace("T", " ") })}
              </p>
              <label className="text-xs uppercase tracking-widest text-neutral-500">{t("miPerfil.nuevaDevolucion")}</label>
              <input
                type="datetime-local"
                className="pc-input"
                value={nuevaFechaDevolucion}
                min={(ampliarFor.fecha_devolucion || "").slice(0, 16)}
                onChange={e => setNuevaFechaDevolucion(e.target.value)}
                data-testid="ampliar-nueva-fecha"
              />
              <div className="flex gap-2 justify-end mt-5">
                <button type="button" onClick={() => setAmpliarFor(null)} className="px-4 py-2 text-sm">{t("miPerfil.cancelar")}</button>
                <button
                  type="button" onClick={confirmarAmpliacion} disabled={ampliando}
                  className="btn-red px-4 py-2 text-sm disabled:opacity-50"
                  data-testid="ampliar-confirmar-btn"
                >
                  {ampliando ? t("miPerfil.procesando") : t("miPerfil.confirmarAmpliacion")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
